/// <reference path="../../../WebUI/typings/tsd.d.ts" />

var MaxScaleUp = 2;   // Small images can be scaled up by a factor of upto this number in order to fit/fill the container.

export interface IImage {
    href?: string;
    width?: number;
    height?: number;
}

export function findWidestImage(images: IImage[]): IImage {
    if (!images || !images.length)
        return null;
    var widest = images[0];
    for (var i = 1; i < images.length; i++) {
        if (images[i].width > widest.width)
            widest = images[i];
    }
    return widest;
}

export enum ImageDisplayMode {
    /** Scales image to fit the size of the parent. Aspect ratio is maintained. Any remaining area of the parent's bounds is transparent. */
    Fit,
    /** Scales the image to fill the size of the parent. Aspect ratio is maintained. Some portion of the image may be clipped to fill the parent's bounds. */
    Fill
}

/** Sets image url to img element, then scales and positions the image inside its parent, as specified by the mode parameter. */
export function setImageSource($img: JQuery, imageUrl: string, mode: ImageDisplayMode): void {
    $img.bind('load', () => {
        scaleAndCenterImage($img, mode);
    });
    $img.attr('src', imageUrl);
}

/** Creates an img element and adds it to the specified container, scaling and positioning as specified by the mode parameter. */
export function addImageToContainer($container: JQuery, imageUrl: string, mode: ImageDisplayMode): void {
    var img = new Image();
    var $img = $(img);
    $img.attr('draggable', 'false');
    $img.bind('load', () => {
        $container.append($img);
        scaleAndCenterImage($img, mode);
    });
    img.src = imageUrl;
}

/** Scales and centers supplied image inside its parent element.
    *  Image must already be loaded before calling this function.
    *  If mode is set to fill then the parent should have overflow: hidden style.
    */
export function scaleAndCenterImage($img: JQuery, mode: ImageDisplayMode): void {
    var $container = $img.parent();
    var containerWidth = $container.width();
    var containerHeight = $container.height();
    var img = <HTMLImageElement>$img[0];
    var scaleX = img.naturalWidth / containerWidth;
    var scaleY = img.naturalHeight / containerHeight;
    var scale: number;
    switch (mode) {
        case ImageDisplayMode.Fit:
            scale = Math.max(scaleX, scaleY);
            break;
        case ImageDisplayMode.Fill:
            scale = Math.min(scaleX, scaleY);
            break;
    }
    if (scale < 1 / MaxScaleUp)
        scale = 1 / MaxScaleUp;
    var w = Math.round(img.naturalWidth / scale);
    var h = Math.round(img.naturalHeight / scale);
    var x = (containerWidth - w) / 2;
    var y = (containerHeight - h) / 2;
    $img.css({ width: w, height: h, marginLeft: Math.round(x) + 'px', marginTop: Math.round(y) + 'px' });
}

/** Loads an image from supplied file.
    *  @maxImageWidth: If a non-zero value is supplied, reduces the image to be no wider than this value.
    */
export function loadImageFromFile(file: any, maxImageWidth: number, callback: (image: IImage) => void): void {
    var reader = new FileReader();
    reader.onload = ev => {
        var imageUrl = (<any>ev.target).result;
        if (maxImageWidth)
            reduceImageSize(imageUrl, maxImageWidth, image => callback(image));
        else
            preloadImages([{ href: imageUrl }], images => callback(images[0]));
    };
    reader.readAsDataURL(file);
}

/** Reduces the size of the image.
    *  @imageUrl: The url of the image. This is typically a data url. Cross-domain urls will cause a security error.
    *  @maxImageWidth: The desired max width of the image. If the width of the image is smaller then the image is not resized.
    */
export function reduceImageSize(imageUrl: string, maxImageWidth: number, callback: (image: IImage) => void): void {
    var $img = $('<img />');
    $img.load(() => {
        var img = <HTMLImageElement>$img[0];
        var canvas = document.createElement("canvas");
        canvas.width = Math.min(img.naturalWidth, maxImageWidth);
        canvas.height = Math.round(img.naturalHeight * canvas.width / img.naturalWidth);
        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        callback({ href: canvas.toDataURL("image/jpeg", 0.8), width: canvas.width, height: canvas.height });
    });
    $img.error(() => {
        callback(null);
    });
    $img.attr('src', imageUrl);
}

/** Load images, with timeout, to get their sizes. */
export function preloadImages(items: IImage[], callback: (items: IImage[]) => void) {
    const status = {        // This is an object because values in it need to be modified by closures.
        loadedCount: 0,
        completed: false
    };
    const elements = [];
    let timeoutId = null;
    function complete(): void {
        if (!status.completed) {
            status.completed = true;
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            $.each(elements, function (elementIndex, $element) {
                $element.unbind();
            });
            const filtered = [];
            $.each(items, function (itemIndex, item) {
                const minImageSize = 32;
                if (item.width >= minImageSize && item.height >= minImageSize) {
                    filtered.push(item);
                }
            });
            callback(filtered);
        }
    }
    const imageCount = items.length;
    if (imageCount === 0) {
        complete();
    }
    else {
        timeoutId = setTimeout(function () {
            timeoutId = null;
            complete();
        }, 8000);
        for (let i = 0; i < items.length; i++) {
            const $img = $("<img data-index='" + i + "'>");
            elements.push($img);
            $img.bind("load", function (e?: JQueryEventObject) {
                const $src: any = $(e.target);
                const index = $src.attr('data-index') * 1;
                items[index].width = (typeof $src[0].naturalWidth === 'number') ? $src[0].naturalWidth : $src[0].width;
                items[index].height = (typeof $src[0].naturalHeight === 'number') ? $src[0].naturalHeight : $src[0].height;
                if (++status.loadedCount === imageCount) {
                    complete();
                }
            });
            $img.bind("error", function (e?: JQueryEventObject) {
                if (++status.loadedCount === imageCount) {
                    complete();
                }
            });
            $img.attr("src", items[i].href);
        }
    }
}

/** Load image, with timeout, to get its size. */
export function preloadImage(href: string, callback: (item: IImage) => void) {
    var images: IImage[] = [{ href: href }];
    preloadImages(images, items => {
        callback(items.length === 1 ? items[0] : null);
    });
}

export function IsImageFile(file: any): boolean {
    return (file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/gif");
}




// WEBPACK FOOTER //
// ./Client/Utils/ImageUtils.ts